import React, {Fragment, memo, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router';
import {batchActions} from 'redux-batched-actions';
import memoizee from 'memoizee';

import {deselectAll} from '@mapsight/core/lib/feature-selections/actions';

import modClasses from '@mapsight/ui/src/js/helpers/mod-classes';
import {viewSelector} from '@mapsight/ui/src/js/store/selectors';
import {VIEW_DESKTOP, VIEW_FULLSCREEN} from '@mapsight/ui/src/js/config/constants/app';
import {FEATURE_SELECTIONS} from '@mapsight/ui/src/js/config/constants/controllers';
import {FEATURE_SELECTION_PRESELECT, FEATURE_SELECTION_SELECT} from '@mapsight/ui/src/js/config/feature/selections';

import {useMagic, useNavPos} from '../../../hooks';

import {navOpenSelector} from '../../store/selectors';
import {deselectAllViaHistory, setNavigationOpen} from '../../store/actions';

import Link from '../link';



// open === undefined wird von side-menu genutzt
export const Entry = memo(
	/**
	 * @param {{
	 *   href: string,
	 *   name: string,
	 *   open: boolean | null | undefined
	 *   hideTab: boolean | null | undefined,
	 * }} props
	 */
	function Entry({href, name, open, hideTab}) {
		const location = useLocation();
		const dispatch = useDispatch();

		const active = location.pathname === href; // ist nie der Fall
		const onClick = useCallback(
			() => {
				const actions = [
					setNavigationOpen(false),
					deselectAll(FEATURE_SELECTIONS, FEATURE_SELECTION_PRESELECT),
					deselectAllViaHistory(FEATURE_SELECTIONS, FEATURE_SELECTION_SELECT),
				];
				// Menü einfach immer schließen statt:
				// if (active) {
				// 	actions.push(setNavigationOpen(false));
				// }
				dispatch(batchActions(actions));
			},
			[dispatch]//, active],
		);

		//if (process.env.NN_JS_LOG_LEVEL === 'verbose') {
		//	console.log('menu entry', {authorization, href, name, isLogin, magic: useMagic()});
		//}

		const l = (<Link
				to={href}
				external={true}
				className="stade-menu__entry-link"
				onClick={onClick}
				tabIndex={open === false || hideTab ? -1 : undefined}
			>
				{name}
			</Link>);

		return (
			<li
				className={modClasses('stade-menu__entry', {active, [name]: true})}
				tabIndex={open===false?-1:undefined}
			>
				{l}
			</li>
		);
	}
);

/** @typedef {{
 *   name: string,
 *   menu: string,
 *   href: string,
 * }} MenuEntryData
 */

export const calculateMenus = memoizee(
	/**
	 * @param {import("../types").Magic} magic
	 * @param {string} lang
	 * @returns {Record<string, {sectionName: string, entries: Array<MenuEntryData>}>}
	 */
	function calculateMenus(magic, lang) {

		/** @type {Record<string, {sectionName: string, entries: Array<MenuEntryData>}>} */
		const sections = {};

		(magic.menu[lang] || magic.menu['de'] || []).forEach(
			({title, url, menu = '', hidden}) => {
				if (hidden !== true) {
					const section = sections[menu] || {sectionName: menu, entries: []};
					sections[menu] = section;
					section.entries.push({
						name: title,
						menu,
						href: `${url}`,
					});
				}
			}
		);

		return sections;
	}
);

const Sections = memo(
	/**
	 * @param {{
	 *   open: boolean | null | undefined,
	 *   hideTab: boolean | null | undefined,
	 * }} props
	 */
	function Sections({open, hideTab}) {
		const magic = useMagic();
		const navPos = useNavPos();

		const menuData = calculateMenus(magic, navPos?.app?.lang);
		const sections = Object.values(menuData).map(({sectionName, entries}) => (
			//// Stade hat immer nur eine Section → auskommentiert
			// <li
			// 	key={sectionName}
			// 	className="stade-menu__section"
			// >
			// 	<span className="stade-menu__section-name">
			// 		{sectionName}
			// 	</span>
				<ul key={sectionName} className="stade-menu__entry-list">
					{entries.map(({href, name}) => (
						<Entry
							key={href}
							href={href}
							name={name}
							open={open}
							hideTab={hideTab}
						/>
					))}
				</ul>
			// </li>
		));

		return (
			<Fragment>
				{sections}
			</Fragment>
		);
	}
);

export default memo(
	/**
	 * @param {{
	 *   buttonRef: React.MutableRefObject<HTMLElement | undefined>,
	 *   isScrollable: boolean,
	 *   isScrolled: boolean,
	 *   forceShowScrollable: boolean,
	 * }} props
	 */
	function Menu({buttonRef, isScrollable, isScrolled, forceShowScrollable}) {
		const openByButton = useSelector(navOpenSelector);
		const view = useSelector(viewSelector);
		const openByView = view === VIEW_DESKTOP || view === VIEW_FULLSCREEN;
		const open = openByButton || openByView;

		const moveFocus = useCallback(
			() => buttonRef.current && buttonRef.current.focus(),
			[buttonRef]
		);

		return (
			<nav
				tabIndex={-1}
				aria-hidden={!open ? true : undefined}
				className={modClasses('stade-menu', {
					open,
					'open-by-view': openByView,
					scrollable: isScrollable,
					'force-show-scrollable': forceShowScrollable,
					'is-scrolled': isScrolled,
				})}
				id="stade-menu-nav"
			>
				{/* Stade hat immer nur eine Section → auskommentiert */}
				{/* <ul className="stade-menu__section-list"> */}
					<Sections open={open} hideTab={isScrollable && !forceShowScrollable}/>
				{/* }</ul> */}
				{buttonRef.current && <i className="visuallyhidden">have buttonRef</i> }
				{buttonRef.current && <button
					aria-hidden={'true'}
					onFocus={moveFocus}
					className="visuallyhidden"
					tabIndex={!open ? -1 : undefined}
				/>}
			</nav>
		);
	}
);
